import { TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

interface DateRangeInputProps {
  label: string;
  startValue: string;
  endValue: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  maxMonths?: number;
}

/**
 * Componente para un par de fechas (inicio/fin)
 * Muestra validación visual con borde rojo
 */
const DateRangeInput: React.FC<DateRangeInputProps> = ({
  label,
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  maxMonths,
}) => {
  const [startError, setStartError] = useState(false);
  const [endError, setEndError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    validateRange(startValue, endValue);
  }, [startValue, endValue, maxMonths]);

  const validateRange = (start: string, end: string) => {
    if (start && !end) {
      setStartError(false);
      setEndError(true);
      setErrorMessage('Debes agregar una fecha de fin');
      return false;
    }

    if (start && end && new Date(start) > new Date(end)) {
      setStartError(true);
      setEndError(true);
      setErrorMessage('La fecha de inicio no puede ser mayor a la de fin');
      return false;
    }

    if (maxMonths !== undefined && start && end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const yearDiff = endDate.getFullYear() - startDate.getFullYear();
      const monthDiff = endDate.getMonth() - startDate.getMonth();
      const diffInMonths = yearDiff * 12 + monthDiff;

      if (diffInMonths > maxMonths) {
        setStartError(true);
        setEndError(true);
        setErrorMessage(
          `La diferencia no puede ser mayor a ${maxMonths} meses`,
        );
        return false;
      }
    }

    setStartError(false);
    setEndError(false);
    setErrorMessage('');
    return true;
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Typography variant="body2" marginBottom={1}>
        {label}
      </Typography>
      <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <TextField
            label="Fecha de inicio"
            type="date"
            size="small"
            value={startValue || ''}
            onChange={(e) => onStartChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={startError}
            fullWidth
          />
          <TextField
            label="Fecha de fin"
            type="date"
            size="small"
            value={endValue || ''}
            onChange={(e) => onEndChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={endError}
            fullWidth
          />
        </div>
        {errorMessage && (
          <Typography variant="caption" color="error" sx={{ marginTop: -0.5 }}>
            {errorMessage}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default DateRangeInput;
